// src/hooks/useAlerts.js
import { useState, useCallback } from 'react';

export const useAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const maxAlerts = 1000;

  const init = useCallback(() => {
    console.log('🔔 Initializing Alert Manager...');
    loadAlertsFromStorage();
  }, []);

  const addAlert = useCallback((source, symbol, message, type = 'info') => {
    const alert = {
      id: Date.now() + Math.random(),
      source,
      symbol,
      message,
      type,
      timestamp: Date.now(),
      read: false
    };

    setAlerts(prev => {
      const newAlerts = [alert, ...prev];
      
      // Limit alerts
      if (newAlerts.length > maxAlerts) {
        return newAlerts.slice(0, maxAlerts);
      }
      
      return newAlerts;
    });

    // Save to storage
    setTimeout(() => saveAlertsToStorage(), 100);

    console.log(`🔔 New alert: ${source} - ${symbol} - ${message}`);
  }, []);

  const markAsRead = useCallback((alertId) => {
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId ? { ...alert, read: true } : alert
      )
    );
    setTimeout(() => saveAlertsToStorage(), 100);
  }, []);

  const removeAlert = useCallback((alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    setTimeout(() => saveAlertsToStorage(), 100);
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
    setTimeout(() => saveAlertsToStorage(), 100);
  }, []);

  // Add alias for component compatibility
  const clearAlerts = clearAllAlerts;

  const getUnreadCount = useCallback(() => {
    return alerts.filter(alert => !alert.read).length;
  }, [alerts]);

  const getTotalAlertsCount = useCallback(() => {
    return alerts.length;
  }, [alerts]);

  const getRecentAlerts = useCallback((limit = 10) => {
    return alerts.slice(0, limit);
  }, [alerts]);

  const getRecentActivities = useCallback(() => {
    return alerts.slice(0, 10).map(alert => ({
      type: alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : 'success',
      icon: alert.type === 'error' ? 'fas fa-exclamation-circle' : 
            alert.type === 'warning' ? 'fas fa-exclamation-triangle' : 'fas fa-info-circle',
      title: `${alert.source}: ${alert.symbol}`,
      time: new Date(alert.timestamp).toLocaleString()
    }));
  }, [alerts]);

  const refreshAlerts = useCallback(() => {
    // This could include logic to refresh alert counts, etc.
    console.log('Refreshing alerts...');
  }, []);

  const loadAlertsFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem('binance_alerts');
      if (stored) {
        const parsedAlerts = JSON.parse(stored);
        setAlerts(parsedAlerts);
      }
    } catch (error) {
      console.error('Failed to load alerts from storage:', error);
    }
  }, []);

  const saveAlertsToStorage = useCallback(() => {
    try {
      localStorage.setItem('binance_alerts', JSON.stringify(alerts));
    } catch (error) {
      console.error('Failed to save alerts to storage:', error);
    }
  }, [alerts]);

  return {
    alerts,
    init,
    addAlert,
    markAsRead,
    removeAlert,
    clearAllAlerts,
    clearAlerts, // Add this for component compatibility
    getUnreadCount,
    getTotalAlertsCount,
    getRecentAlerts,
    getRecentActivities,
    refreshAlerts
  };
};